import React, { useEffect, useState, useCallback } from "react";
import { X, Upload, Trash2, FileJson, Download, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Wave BK Addendum Part C — Template Library dialog.
 *
 * Shows built-in + user-uploaded process flowsheet templates, allows
 * uploading new ones via JSON file, lets the user download the schema
 * + an example, and deletes custom templates.
 */
export default function TemplateLibrary({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/templates`);
      const j = await res.json();
      setItems(j.templates || []);
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  const handleUpload = async (file) => {
    setErrors([]);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("template_file", file);
      const res = await fetch(`${API_BASE}/api/templates/upload`, {
        method: "POST", body: fd,
      });
      const j = await res.json();
      if (!res.ok || j.ok === false) {
        setErrors(j.errors || [j.message || "Upload failed"]);
        toast.error("Template validation failed");
        return;
      }
      toast.success(`Template "${j.name}" uploaded (${j.equipment_count} equipment, ${j.sections} sections)`);
      reload();
    } catch (e) {
      toast.error("Upload error: " + e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (tid) => {
    if (!window.confirm(`Delete custom template "${tid}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/templates/${tid}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Template deleted");
        reload();
      } else {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "Delete failed");
      }
    } catch (e) { toast.error("Delete error: " + e.message); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleUpload(f);
  };

  if (!open) return null;

  const builtIn = items.filter((i) => !i.is_custom);
  const custom = items.filter((i) => i.is_custom);

  return (
    <div
      data-testid="template-library-dialog"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "2rem",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(880px, 100%)", maxHeight: "90vh", overflow: "auto",
          background: "var(--surface, #0a1428)", color: "var(--text, #cfe7ff)",
          border: "1px solid var(--border, #1f3a5c)", borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "16px 20px", borderBottom: "1px solid var(--border, #1f3a5c)",
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              <FileJson size={16} style={{ display: "inline", marginRight: 8, verticalAlign: -2 }} />
              Template Library
            </h2>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              Built-in + custom flowsheet templates · Wave BK Addendum Part C
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost"
              data-testid="template-library-close"
              style={{ borderColor: "transparent" }}>
            <X size={16} />
          </button>
        </div>

        {/* Upload zone */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border, #1f3a5c)" }}>
          <div
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            data-testid="template-library-dropzone"
            style={{
              border: "2px dashed var(--border, #2a4870)",
              borderRadius: 10, padding: "20px", textAlign: "center",
              background: "rgba(0, 200, 255, 0.04)",
            }}
          >
            <Upload size={28} style={{ opacity: 0.7, marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 500 }}>
              Drag &amp; drop a template JSON, or
            </div>
            <label
              data-testid="template-library-upload-btn"
              className="btn btn-primary"
              style={{ display: "inline-block", marginTop: 10, cursor: "pointer" }}
            >
              <input type="file" accept="application/json,.json"
                  style={{ display: "none" }}
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f);
                    e.target.value = "";
                  }}/>
              {uploading ? "Uploading…" : "Browse Files"}
            </label>
            <div style={{ marginTop: 12, fontSize: 12, opacity: 0.75 }}>
              Template must follow Capexara Process Industry Template Schema v1.{" "}
              <a href={`${API_BASE}/api/templates/schema`} target="_blank" rel="noreferrer"
                  data-testid="template-library-download-schema">
                <Download size={12} style={{ verticalAlign: -1 }}/> Schema
              </a>
              {" · "}
              <a href={`${API_BASE}/api/templates/example`} target="_blank" rel="noreferrer"
                  data-testid="template-library-download-example">
                <Download size={12} style={{ verticalAlign: -1 }}/> Example
              </a>
            </div>
          </div>
          {errors.length > 0 && (
            <div data-testid="template-library-errors"
                style={{ marginTop: 12, padding: 12, borderRadius: 8,
                            background: "rgba(255,80,80,0.12)",
                            border: "1px solid rgba(255,80,80,0.4)",
                            fontSize: 12 }}>
              <strong>Validation errors:</strong>
              <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Built-in list */}
        <div style={{ padding: "16px 20px" }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, opacity: 0.85, margin: "0 0 10px" }}>
            Built-in Templates ({builtIn.filter(i => !i.is_pending).length} ready · {builtIn.filter(i => i.is_pending).length} pending)
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
            {builtIn.map((tpl) => (
              <div key={tpl.template_id}
                  data-testid={`template-card-${tpl.template_id}`}
                  style={{
                    padding: 10, borderRadius: 8,
                    background: tpl.is_pending
                      ? "rgba(120,120,120,0.06)" : "rgba(0,200,255,0.06)",
                    border: "1px solid var(--border, #2a4870)",
                    fontSize: 13,
                  }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  {tpl.is_pending
                    ? <Clock size={14} style={{ opacity: 0.5 }}/>
                    : <CheckCircle2 size={14} style={{ color: "#37d399" }}/>}
                  <strong>{tpl.template_name}</strong>
                </div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>
                  {tpl.is_pending
                    ? "Falls back to AI generator"
                    : `${tpl.sections} sections · ${tpl.equipment_count} equipment`}
                </div>
                {tpl.industry && (
                  <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>{tpl.industry}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Custom list */}
        <div style={{ padding: "16px 20px", borderTop: "1px solid var(--border, #1f3a5c)" }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, opacity: 0.85, margin: "0 0 10px" }}>
            Custom Templates ({custom.length})
          </h3>
          {custom.length === 0 ? (
            <div style={{ padding: 16, textAlign: "center", opacity: 0.6, fontSize: 13 }}>
              No custom templates yet. Upload a JSON file above to get started.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {custom.map((tpl) => (
                <div key={tpl.template_id}
                    data-testid={`template-card-${tpl.template_id}`}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: 10, borderRadius: 8,
                      background: "rgba(255,200,80,0.05)",
                      border: "1px solid rgba(255,200,80,0.3)",
                      fontSize: 13,
                    }}>
                  <div>
                    <strong>{tpl.template_name}</strong>
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                      {tpl.sections} sections · {tpl.equipment_count} equipment
                      {tpl.uploaded_at && ` · uploaded ${tpl.uploaded_at.split("T")[0]}`}
                    </div>
                  </div>
                  <button data-testid={`template-delete-${tpl.template_id}`}
                      className="btn btn-ghost"
                      onClick={() => handleDelete(tpl.template_id)}
                      style={{ borderColor: "transparent" }}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {loading && (
          <div style={{ padding: 12, fontSize: 12, opacity: 0.6, textAlign: "center" }}>
            Loading…
          </div>
        )}
      </div>
    </div>
  );
}
